import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import deepEqual from 'deep-equal'

import { dataTypes, sourceTypes, bindingTypes } from '@adalo/constants'
import GooglePlacesInput from 'components/Shared/Forms/GooglePlacesInput'
import Tooltip from 'components/Shared/Tooltip'
import Icon from 'components/Shared/Icon'
import { buildBinding } from 'utils/bindings'
import { getLabel } from 'utils/sources'

import {
  selectObject,
  getComponent,
  getCurrentAppId,
} from '../../../ducks/editor/objects'

import {
  getSimpleTableBindings,
  getListOptions,
  getSortOptions,
  getSources,
} from '../../../ducks/recommender'

import { getApp } from '../../../ducks/apps'
import { getDatasources, getTable } from '../../../ducks/apps/datasources'
import MenuControl from './Libraries/MenuControl'
import SimpleTextControl from './Libraries/SimpleTextControl'
import ListFilters from './Libraries/ListFilters'
import NewTableOption from './Libraries/NewTableOption'
import APIFilters from './APIFilters'
import AdvancedListOptions from './AdvancedListOptions'

import { THEMES } from '../../../constants'

function bindingComparator(val1, val2) {
  const source1 = val1 && val1.source
  const source2 = val2 && val2.source

  if (!source1 || !source2) {
    return false
  }

  return (
    source1.datasourceId === source2.datasourceId &&
    source1.tableId === source2.tableId
  )
}

function filterComparator(val1, val2) {
  val1 = { ...val1, id: null, sort: null }
  val2 = { ...val2, id: null, sort: null }

  return deepEqual(val1, val2)
}

class CommonListControl extends Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    value: PropTypes.object,
    name: PropTypes.string.isRequired,
    appId: PropTypes.string.isRequired,
    componentId: PropTypes.string.isRequired,
    getLabel: PropTypes.func.isRequired,
  }

  handleChangeTable = ({ source }) => {
    const { name, onChange } = this.props

    //new table
    if (source.source === undefined) {
      return
    }

    source = {
      ...source,
      source: {
        ...source.source,
        sort: null,
      },
    }

    onChange({ [name]: source }, { trigger: 'table' })
  }

  handleChangeFilter = ({ filter }) => {
    const { name, onChange, value } = this.props

    const sort = value && value.source && value.source.sort

    const newValue = {
      ...filter,
      source: {
        ...filter.source,
        sort,
      },
    }

    onChange({ [name]: newValue }, { trigger: 'filter' })
  }

  handleChangeSort = newValue => {
    const { name, value, onChange } = this.props

    onChange(
      {
        [name]: {
          ...value,
          source: {
            ...value.source,
            ...newValue,
          },
        },
      },
      { trigger: 'sort' }
    )
  }

  handleChangeLocationSource = sortReference => {
    const { name, value, onChange } = this.props
    const { source } = sortReference
    let binding = null

    delete value?.source?.sort?.reference?.source

    if (
      sortReference.source.type === sourceTypes.FIELD ||
      sortReference.source.type === sourceTypes.INPUT
    ) {
      binding = buildBinding(bindingTypes.SET_LOCATION, sortReference.source)
    }

    onChange(
      {
        [name]: {
          ...value,
          source: {
            ...value.source,
            sort: {
              ...value.source?.sort,
              reference: {
                fallback: value.source?.sort?.reference?.fallback,
                source: { ...source, binding },
              },
            },
          },
        },
      },
      { trigger: 'sort' }
    )
  }

  handleChangeLocationFallback = fallback => {
    const { name, value, onChange } = this.props

    onChange(
      {
        [name]: {
          ...value,
          source: {
            ...value.source,
            sort: {
              ...value.source?.sort,
              reference: {
                ...value.source?.sort?.reference,
                fallback,
              },
            },
          },
        },
      },
      { trigger: 'sort' }
    )
  }

  handleChangeSecondaryFilter = filter => {
    const { name, value, onChange } = this.props

    onChange({
      [name]: {
        ...value,
        source: {
          ...value.source,
          options: {
            ...value.source.options,
            filter,
          },
        },
      },
    })
  }

  handleChangeLimit = ({ limit }) => {
    const { name, value, onChange } = this.props

    onChange({
      [name]: {
        ...value,
        source: {
          ...value.source,
          options: {
            ...value.source.options,
            limit,
          },
        },
      },
    })
  }

  handleChangeAPIFilter = ({ queryParams }) => {
    const { name, value, onChange } = this.props

    onChange({
      [name]: {
        ...value,
        source: {
          ...value.source,
          options: {
            queryParams,
          },
        },
      },
    })
  }

  handleBindingOptionChange = newValue => {
    const { name, onChange, value } = this.props

    return onChange({
      [name]: { ...value, options: { ...value?.options, ...newValue } },
    })
  }

  addNew(tableOptions) {
    const newOption = {
      label: <NewTableOption handleChangeTable={this.handleChangeTable} />,
      value: {},
    }

    return tableOptions?.concat([null, newOption])
  }

  boundTableRemoved = () => {
    const { value, tableOptions } = this.props

    if (!tableOptions) return false

    if (value?.source?.tableId) {
      const { tableId } = value.source
      const table = tableOptions.filter(t => t.value.source.tableId === tableId)

      if (table.length === 1) return false

      return true
    }
  }

  getLocationLabel = source => {
    const { getLocationLabel } = this.props

    if (source) {
      return getLocationLabel(source)
    }

    return ''
  }

  render() {
    let {
      appId,
      componentId,
      object,
      datasource,
      displayName,
      value,
      tableOptions,
      filterOptions,
      sortOptions,
      locationOptions,
      getLabel,
      isAPI,
      isCustomList,
      listControl,
    } = this.props

    if (!listControl) {
      listControl = {
        filter: true,
        sort: true,
        limit: true,
        advancedOptions: true,
      }
    }

    // Check if table was removed
    if (this.boundTableRemoved()) value = undefined

    let sortValue = null
    let locationValue = null
    let fallbackValue = null

    if (value && value.source && value.source.sort) {
      const { reference, ...sort } = value.source.sort
      sortValue = sort

      if (reference) {
        const { fallback, source } = reference
        fallbackValue = fallback

        if (source) {
          const { binding, ...location } = source
          locationValue = location
        }
      }
    }

    const limitValue =
      value &&
      value.source &&
      value.source.options &&
      value.source.options.limit

    if (value && value.source && value.source.collectionId) {
      return (
        <MenuControl
          options={tableOptions}
          name="source"
          displayName={displayName}
          value={value}
          onChange={this.handleChangeTable}
          comparator={bindingComparator}
          getLabel={getLabel}
        />
      )
    }

    let options = tableOptions
    if (datasource?.type !== 'api') options = this.addNew(tableOptions)

    return (
      <div>
        <MenuControl
          options={options}
          name="source"
          displayName={displayName === 'select' ? 'Menu Options' : displayName}
          value={value}
          onChange={this.handleChangeTable}
          comparator={bindingComparator}
          menuTheme={THEMES.DATA}
        />
        {value &&
          (isAPI ? (
            <>
              <APIFilters
                onChange={this.handleChangeAPIFilter}
                value={value?.source?.options?.queryParams}
                name="queryParams"
                objectId={object.id}
              />
            </>
          ) : (
            <>
              {listControl.filter ? (
                <>
                  <MenuControl
                    options={filterOptions}
                    name="filter"
                    displayName="Filter"
                    value={value}
                    getLabel={getLabel}
                    onChange={this.handleChangeFilter}
                    comparator={filterComparator}
                    menuTheme={THEMES.DATA}
                  />
                  <ListFilters
                    onChange={this.handleChangeSecondaryFilter}
                    binding={value}
                    object={object}
                    appId={appId}
                    componentId={componentId}
                  />
                </>
              ) : null}
              {listControl.sort ? (
                <MenuControl
                  options={sortOptions}
                  name="sort"
                  displayName="Sorting"
                  value={sortValue}
                  onChange={this.handleChangeSort}
                  comparator={deepEqual}
                  placeholder="Add sorting..."
                  menuTheme={THEMES.DATA}
                />
              ) : null}
              {listControl.sort &&
              sortValue &&
              sortValue.type === dataTypes.LOCATION ? (
                <>
                  <MenuControl
                    options={locationOptions}
                    getLabel={this.getLocationLabel}
                    name="source"
                    displayName="Sort in relation to..."
                    value={locationValue}
                    onChange={this.handleChangeLocationSource}
                    comparator={deepEqual}
                    placeholder="Select..."
                    menuTheme={THEMES.DATA}
                  />
                  {locationValue ? (
                    <div className="library-inspect-menu-control">
                      {locationValue.type ===
                      sourceTypes.CUSTOM_LOCATION ? null : (
                        <div className="library-inspect-menu-control-header">
                          <p>Fallback address</p>
                          <Tooltip
                            small
                            hideArrow
                            placement="bottom-start"
                            tooltip="The fallback address will be used if the referenced sorting location cannot be found."
                          >
                            <Icon type="help-small" />
                          </Tooltip>
                        </div>
                      )}
                      <GooglePlacesInput
                        panelView
                        appId={appId}
                        input={{
                          name: 'fallback',
                          onChange: this.handleChangeLocationFallback,
                          value: fallbackValue,
                        }}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              {listControl.limit ? (
                <SimpleTextControl
                  inputType="number"
                  name="limit"
                  value={limitValue}
                  onChange={this.handleChangeLimit}
                  displayName="Maximum number of items"
                  placeholder="No Maximum"
                />
              ) : null}
              {listControl.advancedOptions ? (
                <AdvancedListOptions
                  onChange={this.handleBindingOptionChange}
                  value={value}
                  isCustomList={isCustomList}
                />
              ) : null}
            </>
          ))}
      </div>
    )
  }
}

const mapStateToProps = (state, { objectId, value }) => {
  const appId = getCurrentAppId(state)
  const app = getApp(state, appId)
  const componentId = getComponent(state, objectId)?.id
  const object = selectObject(state, objectId)
  let filterOptions = []
  let sortOptions = []
  let locationOptions = []
  let isAPI = false

  const datasource = getDatasources(state, appId)[0]

  if (value) {
    filterOptions = getListOptions({
      state,
      appId,
      componentId,
      objectId,
      object,
      binding: value,
    })

    sortOptions = getSortOptions({
      state,
      appId,
      object,
      binding: value,
    })

    locationOptions = getSources(
      state,
      appId,
      componentId,
      objectId,
      [dataTypes.LOCATION],
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      true
    )

    const { source } = value
    const { datasourceId, tableId } = source || {}
    const table = getTable(state, appId, datasourceId, tableId)

    isAPI = table?.type === 'api'
  }

  return {
    app,
    appId,
    componentId,
    datasource,
    filterOptions,
    object,
    sortOptions,
    isAPI,
    tableOptions: getSimpleTableBindings(state, appId),
    getLabel: binding => {
      return binding && getLabel(state, binding.source, appId, componentId)
    },
    locationOptions,
    getLocationLabel: source =>
      getLabel(state, source, appId, componentId, true),
  }
}

export default withRouter(
  connect(mapStateToProps, { getDatasources })(CommonListControl)
)
